import * as Accordion from '@radix-ui/react-accordion';
import * as Select from '@radix-ui/react-select';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Icon } from '~/components';
import { DropdownItemLabel } from '~/components/DropdownTreeSingleSelect/components/DropdownItemLabel';
import { DropdownItemTrigger } from '~/components/DropdownTreeSingleSelect/components/DropdownItemTrigger';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { cn } from '~/lib/cn';

interface DropdownItemProps extends RadixUiProps {
  depth: number;
  option: InternalDropdownOption;
  selectedOption?: InternalDropdownOption;
  onSelectOption?: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  searchQuery?: string;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownItem({
  depth,
  option,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  searchQuery,
  OptionTemplate,
  radix = {},
}: DropdownItemProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Toggle the accordion open/closed to reflect the isExpanded state.
  useEffect(() => {
    if (!option.isExpanded && triggerRef.current?.dataset.state === 'open') {
      triggerRef.current.click();
    }

    if (option.isExpanded && triggerRef.current?.dataset.state === 'closed') {
      triggerRef.current.click();
    }
  }, [option.isExpanded]);

  const handleOptionMouseDown = useCallback(() => {
    onSelectOption?.(option);
  }, [onSelectOption, option]);

  const handleOptionKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (['Enter', ' '].includes(event.key)) {
        onSelectOption?.(option);
      }
    },
    [onSelectOption, option],
  );

  let OptionLabel = OptionTemplate || DropdownItemLabel;

  return (
    <Accordion.Header
      {...radix.accordionHeader}
      className={cn('relative', radix.accordionHeader?.className)}
    >
      {option.children?.length ? (
        <div className="absolute inset-y-0 end-0 flex items-center">
          <DropdownItemTrigger triggerRef={triggerRef} option={option} />
        </div>
      ) : null}
      <Select.Item
        {...radix.selectItem}
        value={option.value}
        className={cn(
          'flex cursor-pointer gap-xs px-md py-sm transition-colors duration-300 text-default',
          'hover:bg-emphasis-strong focus:outline-none focus:bg-emphasis-strong',
          {
            'focus:bg-secondary-strong bg-primary-default hover:bg-secondary-default':
              selectedOption?.value === option.value,
          },
          radix.selectItem?.className,
        )}
        style={{
          paddingLeft: depth
            ? `calc(var(--spacing-md) * ${depth + 1})`
            : undefined,
          ...radix.selectItem?.style,
        }}
        onMouseDown={handleOptionMouseDown}
        onKeyDown={handleOptionKeyDown}
        asChild
        aria-selected={selectedOption?.value === option.value}
      >
        <div>
          {showCheckIcon ? (
            <div className="-ml-xs w-md">
              <Select.ItemIndicator>
                <Icon type="check" size="sm" />
              </Select.ItemIndicator>
            </div>
          ) : null}
          <Select.ItemText>
            <OptionLabel option={option} searchQuery={searchQuery} />
          </Select.ItemText>
        </div>
      </Select.Item>
    </Accordion.Header>
  );
}

DropdownItem.displayName = 'DropdownItem';
